import {
  Dialect,
  ExampleContext,
  TransformFn,
  TransformResult,
} from './types.js'

export const AsyncFunction = (async () => {
  /* placeholder */
}).constructor

/**
 * Thrown by `rewriteImports` when an example has a static import the environment
 * can't satisfy (a non-context package with no import-resolver). This is NOT a code
 * defect — the code is fine, the doc environment just doesn't provide the dependency
 * — so the build check treats it as a *warning* (the block is display-only) rather
 * than a fatal error, and can tell it apart from a real syntax/transpile error.
 */
export class UnsupportedImportError extends Error {
  constructor(
    message: string,
    /** The offending `import …` statement. */
    readonly statement: string
  ) {
    super(message)
    this.name = 'UnsupportedImportError'
  }
}

/**
 * Sanitize a context module key into a JS identifier used as the binding name in rewritten
 * imports and as the AsyncFunction parameter. Must be applied consistently on both sides —
 * e.g. `'tosijs-ui'` -> `tosijsui`, `'@babylonjs/core'` -> `babylonjscore`.
 *
 * THE ONE COPY. `test-harness.ts` carried its own `key.replace(/-/g, '')`, which stripped
 * hyphens and left slashes and `@` — so a perfectly ordinary specifier like
 * `'tosijs-3d/demo-utils'` became the parameter name `tosijs3d/demoutils` and every test in
 * that file died with V8's "Arg string terminates parameters early" (tosijs-ui#111/#112).
 * The examples on the same page rendered fine, because THEY used this function. Two copies of
 * one rule, and only one of them maintained.
 *
 * A context key is an import specifier — a string. Nothing about `'@scope/pkg'` suggests it
 * must also be a valid identifier, so this makes one rather than demanding one.
 */
export function contextVarName(key: string): string {
  const stripped = key.replace(/[^a-zA-Z0-9_$]/g, '')
  /*
  A leading digit is still not an identifier, and `'3d-tools'` is a name someone will pick.
  Reserved words are the same problem wearing a different hat — `'class'`, `'new'`, `'import'`
  are all plausible package names. Prefixing is enough for both, and keeps the result readable
  in a stack trace, which a hash would not.
  */
  if (!stripped) return '_ctx'
  if (/^[0-9]/.test(stripped) || RESERVED.has(stripped)) return `_${stripped}`
  return stripped
}

/*
Parameter names, not general JS: only words that cannot appear in a parameter list matter here.
`await` is included because a doc test body is async.
*/
const RESERVED = new Set(
  (
    'break case catch class const continue debugger default delete do else enum export extends ' +
    'false finally for function if implements import in instanceof interface let new null ' +
    'package private protected public return static super switch this throw true try typeof ' +
    'var void while with yield await'
  ).split(' ')
)

/**
 * Turn context keys into a parameter list a Function constructor will accept.
 *
 * Sanitizing each key independently is not sufficient: `'tosijs-3d'` and `'tosijs/3d'` both
 * reduce to `tosijs3d`, and duplicate parameter names are a SyntaxError in a strict body — which
 * a doc test is.
 *
 * A collision THROWS, and deliberately does not quietly rename. `rewriteImports` derives its
 * binding from the same rule, so a suffixed parameter (`tosijs3d2`) would leave one of the two
 * modules bound to a name no rewritten import ever references — importing from it would return
 * undefined, at runtime, with nothing to read. Two specifiers that reduce to one identifier is a
 * genuine ambiguity in the configuration, and the only useful thing to do with an ambiguity is
 * name it. The message is the whole point: "Arg string terminates parameters early" is what this
 * replaces.
 */
export function contextParamNames(keys: string[]): string[] {
  const byName = new Map<string, string>()
  return keys.map((key) => {
    const name = contextVarName(key)
    const already = byName.get(name)
    if (already !== undefined) {
      throw new Error(
        `example context keys ${JSON.stringify(already)} and ${JSON.stringify(
          key
        )} both reduce to the identifier "${name}", so an import from one of them could not be ` +
          `told from the other. Rename one of the context keys.`
      )
    }
    byName.set(name, key)
    return name
  })
}

/**
 * Rewrite import statements (from the example context) to const bindings:
 *   import { x } from 'tosijs'        -> const { x } = tosijs
 *   import * as B from '@babylonjs'   -> const B = babylonjs   (context key)
 *   import Foo from 'my-lib'          -> const Foo = mylib
 * The `.elements` accessor form (`import { x } from 'tosijs'.elements`) is
 * preserved. Any static import that isn't from a context module (or uses an
 * unsupported form) throws a clear error rather than becoming a SyntaxError in
 * the AsyncFunction body.
 */
/**
 * Turn the import specifiers a `X as Y` clause carries into destructuring pairs:
 * `a, b as c` → `a, b: c`.
 */
function destructureClause(names: string): string {
  return names
    .split(',')
    .map((n) => {
      const as = n.trim().match(/^(\w+)\s+as\s+(\w+)$/)
      return as ? `${as[1]}: ${as[2]}` : n.trim()
    })
    .filter(Boolean)
    .join(', ')
}

/**
 * Rewrite the import statements the context DIDN'T inject into dynamic imports the
 * import-resolver service worker can fulfil — a bare specifier `pkg` becomes
 * `await import('<prefix>pkg')`, a `./x` / `https://…` specifier a direct dynamic
 * import. This is what lets a live example pull real npm code from anywhere; it only
 * runs when the resolver is enabled (a `prefix` is known). See import-resolver-plan.md.
 */
function rewriteBareImportsToDynamic(code: string, prefix: string): string {
  const urlFor = (spec: string) =>
    /^(\.|\/|https?:)/.test(spec) ? spec : prefix + spec
  return (
    code
      // import <clause> from '<spec>'   ({ a, b } | * as X | X | X, { a })
      // NB the trailing `[ \t]*;?` must NOT eat the newline, or the next line glues on.
      .replace(
        /import\s+([^;'"]+?)\s+from\s+(['"])([^'"]+)\2[ \t]*;?/g,
        (match, clause: string, q: string, spec: string) => {
          const imp = `await import(${q}${urlFor(spec)}${q})`
          const c = clause.trim()
          let m
          if ((m = c.match(/^\{([^}]*)\}$/)))
            return `const { ${destructureClause(m[1])} } = ${imp}`
          if ((m = c.match(/^\*\s+as\s+(\w+)$/)))
            return `const ${m[1]} = ${imp}`
          if ((m = c.match(/^(\w+)$/)))
            return `const ${m[1]} = (${imp}).default`
          if ((m = c.match(/^(\w+)\s*,\s*\{([^}]*)\}$/)))
            return `const { default: ${m[1]}, ${destructureClause(
              m[2]
            )} } = ${imp}`
          return match // unhandled form — left to fail loudly below
        }
      )
      // side-effect only: import '<spec>'
      .replace(
        /import\s+(['"])([^'"]+)\1[ \t]*;?/g,
        (_m, q: string, spec: string) => `await import(${q}${urlFor(spec)}${q})`
      )
  )
}

/** The import-resolver prefix, if the SW was registered on this page (runtime). */
function resolverPrefix(): string | undefined {
  return (
    globalThis as unknown as { __TOSI_IMPORT_RESOLVER?: { prefix?: string } }
  ).__TOSI_IMPORT_RESOLVER?.prefix
}

/**
 * Fail with a sentence, not a destructuring error, when an example imports a module the site
 * listed in its context but never supplied (#210 item 4).
 *
 * `bundleEntry` REPLACES tosijs-ui's bundle, and with it the `xinjs` / `xinjsui` globals the
 * doc system reads its default context from. The key is then present and its value
 * `undefined`, so `import { elements } from 'tosijs-ui'` became `const { elements } =
 * undefined` and the reader was told "Cannot destructure property 'elements' of 'tosijsui'"
 * about a line they did not write.
 */
export function assertContextProvided(
  code: string,
  context: Record<string, unknown>
): void {
  for (const [moduleName, value] of Object.entries(context)) {
    if (value != null) continue
    const m = moduleName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // A real import STATEMENT: `import` opening a statement, then `from 'mod'` before any
    // quote or semicolon. A commented-out import, or the words inside a string, are not one —
    // and an example carrying either ran fine before this check existed, so it must still.
    const statement = new RegExp(
      `(?:^|[;\\n])[ \\t]*import\\b[^;'"]*?\\bfrom\\s*['"]${m}['"]`
    )
    if (!statement.test(code)) continue
    throw new Error(
      `This example imports from '${moduleName}', but this site's bundle does not ` +
        `provide '${moduleName}' to examples. A custom bundleEntry replaces the bundle ` +
        `that used to supply it. In the bundle entry, import the module and hand it to ` +
        `the doc system — for tosijs-ui: \`import * as tosijsUi from 'tosijs-ui'\` then ` +
        `\`globalThis.xinjsui = tosijsUi\` (or set <tosi-doc-system>'s \`context\`).`
    )
  }
}

/**
 * `rewriteImports` for code that is about to RUN against a context: checks first that every
 * module it imports was actually supplied (`assertContextProvided`). Tests, inline tjs tests
 * and `executeCode` go through this. The example itself pairs the two calls by hand in
 * execution.ts, because a pre-compiled example skips the rewrite but still needs the check.
 * The REPL deliberately uses plain `rewriteImports`: a REPL line is not a module.
 */
export function rewriteContextImports(
  code: string,
  context: Record<string, unknown>
): string {
  assertContextProvided(code, context)
  return rewriteImports(code, Object.keys(context))
}

export function rewriteImports(
  code: string,
  contextKeys: string[],
  // The import-resolver prefix (e.g. '/lib/'). When set — or when the SW is registered
  // on the page — non-context imports become dynamic imports the worker resolves.
  // Omitted + no SW → the old behavior: a non-context import is unsupported.
  importPrefix: string | undefined = resolverPrefix()
): string {
  let result = code
  for (const moduleName of contextKeys) {
    const js = contextVarName(moduleName)
    const m = moduleName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // import { a, b } from 'mod'
    result = result.replace(
      new RegExp(`import\\s*\\{([^}]*)\\}\\s*from\\s*['"]${m}['"]`, 'g'),
      (_, names: string) =>
        `const { ${names.replace(/\s+/g, ' ').trim()} } = ${js}`
    )
    // import * as X from 'mod'
    result = result.replace(
      new RegExp(`import\\s*\\*\\s*as\\s+(\\w+)\\s+from\\s*['"]${m}['"]`, 'g'),
      (_, name: string) => `const ${name} = ${js}`
    )
    // import X from 'mod'  (default)
    result = result.replace(
      new RegExp(`import\\s+(\\w+)\\s+from\\s*['"]${m}['"]`, 'g'),
      (_, name: string) => `const ${name} = ${js}`
    )
  }
  // With the resolver on, route the imports the context didn't inject to the SW.
  if (importPrefix) {
    result = rewriteBareImportsToDynamic(result, importPrefix)
  }
  // Anything still a static import is unsupported — fail loudly with the line.
  const leftover = result.match(/^\s*import\s+['"{*\w][^\n]*/m)
  if (leftover) {
    const statement = leftover[0].trim()
    /*
    Name the token we choked on, not the grammar we accept (#141).

    The old message listed the supported FORMS and the context keys — so a failing
    `import { x } from "pkg"`, which is a listed package in the `{ named }` form, was
    answered with a sentence that ruled out its own cause and pointed at three things that
    were all fine. It cost the reporter ~40 minutes and sent them to rewrite a multiline
    import that was never the problem.

    The two causes are different and now say so: a specifier the context does not carry, vs
    a clause shape the rewriter does not handle.
    */
    const spec = statement.match(/from\s*['"]([^'"]+)['"]/)?.[1]
    const known = spec !== undefined && contextKeys.includes(spec)
    const detail = known
      ? `\`${spec}\` IS in the example context, so the import CLAUSE is what could not be ` +
        `rewritten — supported forms are \`{ named }\`, \`* as ns\`, \`default\`, and ` +
        `\`default, { named }\`.`
      : `${
          spec ? `\`${spec}\` is not` : 'that specifier is not'
        } in the example context (${contextKeys.join(', ')})` +
        (importPrefix
          ? `, and the import-resolver did not rewrite it.`
          : `. Enable importResolver to import other packages.`)
    throw new UnsupportedImportError(
      `live example: unsupported import \`${statement}\` — ${detail}`,
      statement
    )
  }
  return result
}

/**
 * Execute code as an async function with injected context
 */
export async function executeCode(
  code: string,
  context: ExampleContext,
  transform: TransformFn
): Promise<void> {
  const rewrittenCode = rewriteContextImports(code, context)
  const transformedCode = (
    await transform(rewrittenCode, {
      transforms: ['typescript'],
    })
  ).code

  const contextKeys = contextParamNames(Object.keys(context))
  const contextValues = Object.values(context)

  // @ts-expect-error AsyncFunction constructor typing
  const func = new AsyncFunction(...contextKeys, transformedCode)
  await func(...contextValues)
}

/** The tjs() entry from tjs-lang/lang — the only part we need at the JS seam. */
type TjsFn = (
  source: string,
  options?: { dialect?: 'js' | 'tjs'; runTests?: boolean | 'only' | 'report' }
) => { code: string; testRunner?: unknown; prelude?: unknown; body?: unknown }

/**
 * The fromTS() entry from tjs-lang/browser/from-ts — lowers TypeScript to tjs.
 * Async: it lazy-loads the TypeScript compiler (from a CDN) on first call.
 */
type FromTsFn = (
  source: string,
  options?: {
    emitTJS?: boolean
    /**
     * Which TypeScript build to lazy-load. Declared here because this local type
     * mirrors the BROWSER entry (`BrowserFromTSOptions`), not the node
     * `FromTSOptions` — only the browser one carries it, and omitting it silently
     * falls back to tjs-lang's unpinned `esm.sh/typescript@5`.
     */
    typescriptUrl?: string
  }
) => Promise<{ code: string }>

// tjs-lang/browser is a SELF-CONTAINED transpiler bundle (acorn/tosijs-schema
// inlined, zero bare imports), so it loads as a raw CDN file — no `+esm` rewrite
// needed. The TypeScript path lives behind tjs-lang/browser/from-ts (also
// self-contained) and is loaded only for `ts` examples; from-ts in turn fetches
// the TypeScript compiler lazily at runtime, so tsc is never pulled in until a TS
// example actually transforms.
//
// PINNED TO MATCH THE DEV DEP, and the two live in different files — this constant and
// package.json's exact `tjs-lang` entry. `code-transform.test.ts` asserts they agree, because
// a CDN version that drifts from the installed one is invisible until it isn't: the tests run
// against the local bundle and the published site runs against whatever this string says.
// Flagged by tjs-lang in tosijs-ui#135, alongside noticing we had sat on a deprecated 0.13.4.
export const TJS_VERSION = '0.14.0-rc.5'

/*
The TypeScript compiler a live `ts` example is transpiled with.

tjs-lang's `fromTS` lazy-loads it from `DEFAULT_TYPESCRIPT_URL` — `https://esm.sh/typescript@5`,
an unpinned major range — and that was the ONE hop in the whole live-example chain that was
neither same-origin nor pinned. Everything else routes through `__TJS_LOCAL_BASE` or a
`TJS_VERSION`-pinned CDN. It is invisible to `bun.lock` and therefore to `bun audit`, so
"which compiler ran in a reader's browser" had no answer and could change under us.

Pinned to the exact version this repo type-checks with (`typescript` devDep), so an example
is lowered by the same compiler that validates the source it came from. **Bump the two
together** — the devDep is the source of truth, this is the browser's copy of it.

esm.sh is kept as the host: tjs-lang measured it as the only CDN that reliably serves
typescript as ESM (jsDelivr `+esm` and esm.run time out on its ~10MB CommonJS; skypack is
dead). The fix here is the pin, not the host. A same-origin copy would be better still and is
the upstream ask — tjs-lang exposes `typescriptUrl` for exactly that.
*/
export const TYPESCRIPT_VERSION = '5.9.3'
export const TYPESCRIPT_URL = `https://esm.sh/typescript@${TYPESCRIPT_VERSION}`

// Where to fetch a tjs-lang browser bundle from, in priority order:
//  1. SAME-ORIGIN — the doc-site build copies the bundles next to the iife and
//     sets `__TJS_LOCAL_BASE`, so the transpiler ships in lockstep with the page,
//     works offline, and is immune to CDN propagation lag. Preferred when present.
//  2. CDN chain (jsdelivr → unpkg → esm.sh) — for IIFE consumers who don't serve
//     it same-origin. A freshly-published version 404s on one CDN until it caches
//     it (minutes–hours) and any one can blip, so we try several. The module-cache
//     service worker caches all three hosts.
function bundleUrls(file: string): string[] {
  const localBase = (globalThis as { __TJS_LOCAL_BASE?: string })
    .__TJS_LOCAL_BASE
  return [
    ...(typeof localBase === 'string' ? [`${localBase}${file}`] : []),
    `https://cdn.jsdelivr.net/npm/tjs-lang@${TJS_VERSION}/dist/${file}`,
    `https://unpkg.com/tjs-lang@${TJS_VERSION}/dist/${file}`,
    `https://esm.sh/tjs-lang@${TJS_VERSION}/dist/${file}`,
  ]
}

/** Try each URL in turn; return the first module that loads, else null. */
async function importFirstAvailable(urls: string[]): Promise<any | null> {
  for (const url of urls) {
    try {
      // variable specifier → bundlers leave it as a runtime import (external)
      const m = await import(/* webpackIgnore: true */ /* @vite-ignore */ url)
      if (m) return m
    } catch {
      // try the next source
    }
  }
  return null
}

async function loadTjs(): Promise<TjsFn | null> {
  // Installed peer (ESM consumers / dev build) — static specifier so bundlers
  // resolve it to the local package.
  try {
    const m = (await import('tjs-lang/browser')) as {
      tjs: TjsFn
      stripExports?: unknown
    }
    if (typeof m.tjs === 'function') {
      noteStripExports(m.stripExports)
      return m.tjs
    }
  } catch {
    // not installed — try same-origin / CDN
  }
  const m = await importFirstAvailable(bundleUrls('tjs-browser.js'))
  if (m && typeof m.tjs === 'function') {
    noteStripExports(m.stripExports)
    return m.tjs as TjsFn
  }
  return null
}

/*
tjs-lang's parser-based `stripExports` (#210 item 5), from the same module `tjs` came from.

An example runs as a function body, where `export` is a syntax error, so one written as a
module could not run. Removing `export` has to be done by parsing (a line regex rewrites a
template literal that happens to start a line with `export `), tosijs-ui ships no JavaScript
parser, and tjs-lang's browser bundle already carries one. FEATURE-DETECTED: it is exported
from 0.14.0-rc.4 on, so with an older tjs-lang an `export` fails as it always has.
*/
type StripExports = (code: string) => string
let loadedStripExports: StripExports | undefined
function noteStripExports(candidate: unknown): void {
  if (typeof candidate === 'function')
    loadedStripExports = candidate as StripExports
}
// A test's stand-in sits BESIDE what was loaded, never over it: tjs-lang loads once per
// process, so a stand-in that replaced it could not be undone.
let stripExportsOverride: StripExports | null | undefined
const currentStripExports = (): StripExports | undefined =>
  stripExportsOverride === undefined
    ? loadedStripExports
    : stripExportsOverride ?? undefined

/**
 * @internal For TESTS only: a function stands in for tjs-lang's `stripExports`, `null` acts
 * as a tjs-lang that has none, and `undefined` goes back to whatever was loaded.
 */
export function setStripExportsForTests(
  stand: StripExports | null | undefined
): void {
  stripExportsOverride = stand
}

/**
 * `code` without its `export`s, when the tjs-lang this process can load is able to remove
 * them; otherwise `code` unchanged. For a caller that transpiles by some other route (the
 * build check lowers `ts` with Bun, not with tjs-lang) and must still answer "will this
 * run?" the way the page does.
 */
export async function withoutExports(code: string): Promise<string> {
  if (!/\bexport\b/.test(code)) return code
  await (tjsOnce ??= loadTjs())
  const strip = currentStripExports()
  return strip ? strip(code) : code
}

/**
 * Generated documentation for a `tjs` source (#184 part 3): tjs-lang's
 * `generateDocsMarkdown(source, tjs(source).types)`, where the signature IS the docs.
 *
 * FEATURE-DETECTED: tjs-lang's browser bundle exports the generator from the 0.14 release
 * candidates on (the pinned 0.14.0-rc.5 has it; 0.13.13 did not). With an older tjs-lang
 * this resolves to null and the built-in `tjs` shows no Docs tab; such a site can supply
 * docs itself through `registerDialect('tjs', { transform, docs })`.
 */
export type DocsFn = (source: string) => string

let tjsDocsOnce: Promise<DocsFn | null> | undefined
export function loadTjsDocs(): Promise<DocsFn | null> {
  return (tjsDocsOnce ??= (async () => {
    const sources: Array<() => Promise<any>> = [
      () => import('tjs-lang/browser'),
      () => importFirstAvailable(bundleUrls('tjs-browser.js')),
    ]
    for (const load of sources) {
      try {
        const m = await load()
        if (
          m &&
          typeof m.generateDocsMarkdown === 'function' &&
          typeof m.tjs === 'function'
        ) {
          return (source: string) => {
            const { types } = m.tjs(source, {
              dialect: 'tjs',
              runTests: false,
            })
            // Nothing declared, nothing to document, so no Docs tab. The generator would
            // answer with a sentence saying so, and most examples declare no function.
            if (!types || Object.keys(types).length === 0) return ''
            return m.generateDocsMarkdown(source, types) as string
          }
        }
      } catch {
        // try the next source
      }
    }
    return null
  })())
}

/**
 * tjs inline-test API (from tjs-lang/lang):
 *   extractTests(src) → { code (test-stripped), tests, testRunner }
 *   testUtils — a string defining `expect`/`assert` etc. for the runner
 * Run with: `new AsyncFunction(...ctx, execJs + testUtils + 'return ' + testRunner)`
 * which resolves to `{ passed, failed, results }`.
 */
export interface TjsTestApi {
  extractTests: (source: string) => {
    code: string
    tests: { description: string }[]
    testRunner: string
  }
  testUtils: string
}

export interface TjsTestResult {
  passed: number
  failed: number
  results: { description: string; passed: boolean; error?: string }[]
}

let testApiOnce: Promise<TjsTestApi | null> | undefined

async function loadTjsTestApiImpl(): Promise<TjsTestApi | null> {
  const sources: Array<() => Promise<any>> = [
    () => import('tjs-lang/browser'),
    () => importFirstAvailable(bundleUrls('tjs-browser.js')),
  ]
  for (const load of sources) {
    try {
      const m = (await load()) as Partial<TjsTestApi> | null
      if (
        m &&
        typeof m.extractTests === 'function' &&
        typeof m.testUtils === 'string'
      ) {
        return { extractTests: m.extractTests, testUtils: m.testUtils }
      }
    } catch {
      // try next source
    }
  }
  return null
}

/** Load the tjs inline-test API (memoized). null if tjs-lang is unavailable. */
export function loadTjsTestApi(): Promise<TjsTestApi | null> {
  return (testApiOnce ??= loadTjsTestApiImpl())
}

// from-ts pulls in the TypeScript compiler, so it's loaded lazily and only when a
// `ts` example is actually transformed — never for `js`/`tjs` pages.
async function loadFromTs(): Promise<FromTsFn | null> {
  try {
    const { fromTS } = (await import('tjs-lang/browser/from-ts')) as {
      fromTS: FromTsFn
    }
    if (typeof fromTS === 'function') return fromTS
  } catch {
    // not installed — try same-origin / CDN
  }
  const m = await importFirstAvailable(bundleUrls('tjs-browser-from-ts.js'))
  if (m && typeof m.fromTS === 'function') return m.fromTS as FromTsFn
  return null
}

// Load tjs once per page, not once per example. refresh() runs on every render
// (and renders fire repeatedly while tests run), so a per-call import + parse
// would re-pay tjs's cost each time and make every preview swap visibly lag —
// the engine load is memoized and transform output is cached by dialect+source.
let tjsOnce: Promise<TjsFn | null> | undefined
let fromTsOnce: Promise<FromTsFn | null> | undefined
/**
 * Memoized transform output, keyed by dialect + full source text.
 *
 * BOUNDED, and it has to be. The key contains the whole source, so a *changed*
 * example is a new entry and the superseded one is never looked up again — the cache
 * only ever grows, one entry per version of every example ever transformed. On a page
 * you merely read, that is a handful of entries. But this module is also imported by
 * the doc-site BUILD (check-examples), and — more to the point — the doc system is an
 * *authoring* system: in an edit-in-place session, every keystroke-to-save produces a
 * fresh source string, so an unbounded map grows for as long as the page is open,
 * holding both the source and its transpiled output forever.
 *
 * A plain insertion-ordered eviction is enough here: re-transforming a cold example
 * costs a few ms, and the working set is "the examples on this page".
 */
const RESULT_CACHE_MAX = 256
/**
 * What an example keeps of a tjs-lang transpile, with `export` removed when tjs-lang can
 * do that (see `noteStripExports`).
 *
 * The build's example check has a SECOND route for `ts` (it lowers with Bun, because it
 * cannot fetch the TypeScript compiler; `check-examples.ts`), so a rule added here that
 * decides whether an example runs must be added there too. `withoutExports` is that pairing
 * for `export`, and it was missed the first time.
 *
 * Keeps tjs-lang's `testRunner` beside the code when it returns one (#210 item 1). It is one
 * property read on a transpile that already happened, and it is the only runner built from
 * the test bodies as tjs-lang REWROTE them; `extractTests` builds its own from raw text.
 * Keeps `prelude` and `body` when it returns both (#216), for the console.
 */
export function shapeTjsOutput(
  out: {
    code: string
    testRunner?: unknown
    prelude?: unknown
    body?: unknown
  },
  // `null` is "this tjs-lang cannot"; leaving it out asks the loaded one.
  stripExports: StripExports | null | undefined = currentStripExports()
): TransformResult {
  // Parse only what could hold one: most examples have no `export` at all.
  const strip = (code: string): string =>
    stripExports && /\bexport\b/.test(code) ? stripExports(code) : code
  const result: TransformResult = { code: strip(out.code) }
  if (typeof out.testRunner === 'string') result.testRunner = out.testRunner
  if (typeof out.prelude === 'string' && typeof out.body === 'string') {
    result.prelude = out.prelude
    result.body = strip(out.body)
  }
  return result
}

const resultCache = new Map<string, TransformResult>()
const cacheResult = (key: string, result: TransformResult) => {
  if (resultCache.size >= RESULT_CACHE_MAX) {
    // Map preserves insertion order, so the first key is the oldest.
    const oldest = resultCache.keys().next().value
    if (oldest !== undefined) resultCache.delete(oldest)
  }
  resultCache.set(key, result)
}
let warnedNoTjs = false
let warnedNoFromTs = false

/**
 * Load a live-example transform for a given source dialect.
 *
 * tjs-lang is the engine:
 * - `js`  → `tjs(code, { dialect: 'js' })`, which leaves vanilla JavaScript
 *           untouched (no footgun rewriting) — behavior-neutral for plain JS.
 * - `tjs` → `tjs(code, { dialect: 'tjs' })`, the full tjs lowering (structural
 *           `==`, type guards, runtime instrumentation).
 * - `ts`  → `fromTS(code)` → tjs source → `tjs(…)` with NO dialect, so the annotation
 *           `fromTS` writes decides: converted TypeScript keeps JavaScript's semantics
 *           (no runtime parameter guards, `new` allowed). The TypeScript compiler is
 *           loaded lazily, only here.
 *
 * The dialect is baked into the returned closure, so callers just pass code.
 *
 * Degraded mode: if tjs-lang can't be loaded, plain JS still runs unchanged
 * (`dialect: 'js'` is a no-op on it), so we pass the code through. A `ts` page
 * with no from-ts available likewise falls back to running the source as JS.
 */
/**
 * Is a REAL transform available for this dialect, or would `loadTransform` degrade?
 *
 * `loadTransform` falls back to identity when tjs-lang cannot be resolved, which is right at
 * RUNTIME — an example that will not transpile should still render as something. It is wrong
 * for a build-time CHECK: identity means the block is then parsed as raw JavaScript, so valid
 * TJS produces a confident syntax error attributed to the author's document.
 *
 * That is what happened (tosijs-ui#154): ~30 "errors" in correct TJS, each advising the
 * author to fix code that was already right, with the one line explaining the degradation
 * printed far away. Callers that DIAGNOSE must ask this first and skip what they cannot
 * actually check.
 */
export async function transformAvailable(dialect: Dialect): Promise<boolean> {
  if (dialect === 'js') return true
  if (!(await (tjsOnce ??= loadTjs()))) return false
  if (dialect === 'ts') return Boolean(await (fromTsOnce ??= loadFromTs()))
  return true
}

export async function loadTransform(
  dialect: Dialect = 'js'
): Promise<TransformFn> {
  // `js` needs no transpiler. tjs's `js` dialect leaves vanilla JS untouched, and
  // the build check guarantees js/`test` blocks ARE vanilla JS (a TS-typed one
  // fails `new AsyncFunction` at build). So identity is behaviorally exact — and it
  // keeps the tjs bundle off the first-paint path for the common all-`js`-examples
  // page. `tjs`/`ts` still load it. See self-contained-examples-plan.md.
  if (dialect === 'js') return (code) => ({ code })
  const tjs = await (tjsOnce ??= loadTjs())
  if (!tjs && !warnedNoTjs) {
    warnedNoTjs = true
    console.warn(
      'tjs-lang not available — live examples run as raw JavaScript ' +
        '(tjs/TypeScript examples will not transpile). Install with: npm install tjs-lang'
    )
  }
  const fromTS = dialect === 'ts' ? await (fromTsOnce ??= loadFromTs()) : null
  if (dialect === 'ts' && !fromTS && !warnedNoFromTs) {
    warnedNoFromTs = true
    console.warn(
      'tjs-lang/browser/from-ts not available — `ts` examples run as raw JavaScript.'
    )
  }
  return (code) => {
    const cacheKey = `${dialect}\0${code}`
    const cached = resultCache.get(cacheKey)
    if (cached) return cached
    // runTests:false — examples must not run tjs inline tests at transpile time
    // (the default throws on failure, which would break the example render).
    if (!tjs) {
      const result = { code }
      cacheResult(cacheKey, result)
      return result
    }
    if (dialect === 'ts') {
      // async: fromTS lazy-loads the TypeScript compiler on first use.
      return (async () => {
        const tjsSource = fromTS
          ? (
              await fromTS(code, {
                emitTJS: true,
                typescriptUrl: TYPESCRIPT_URL,
              })
            ).code
          : code
        /*
        NO `dialect` here (#210 item 2). `fromTS` emits TJS carrying a `tjs <- …` annotation
        that gives converted TypeScript JavaScript's semantics, and an explicit `dialect` is
        authoritative: passing `'tjs'` overrode the annotation, so a TS example's
        `new Calculator(…)` was refused as native TJS. tjs-lang's own playground and tests
        call it this way.
        */
        const result = shapeTjsOutput(tjs(tjsSource, { runTests: false }))
        cacheResult(cacheKey, result)
        return result
      })()
    }
    const result = shapeTjsOutput(tjs(code, { dialect, runTests: false }))
    cacheResult(cacheKey, result)
    return result
  }
}
